// ============================================================
// NamedGrantsPanel.jsx  (2026-10-05)
// User-detail panel for the four NAMED grants that replaced the RM/NI
// designation (decisions.md "Authority comes from GRANTS that are on by
// default"): sign off audits, change CarePlan settings, create or edit care
// tasks, and download the termination data export.
//
// Managers hold all four BY DEFAULT; an operator removes one only when the
// provider asks, and that removal is FINAL (the defaults never put it back).
// Rendered in the Agent Access tab beside the module-role panels; reuses the
// cp-role-panel styling. Backend is the source of truth — no optimistic UI.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RotateCw, ShieldCheck } from 'lucide-react';
import Badge from './Badge';
import ConfirmDialog from './ConfirmDialog';
import { toast } from './Toaster';
import { getAuthorityGrants, grantAuthority, revokeAuthority } from '../api/authorityGrants';
import { translateError } from '../utils/errorMessages';
import StatusMessage from './StatusMessage';
import { formatDate } from '../utils/format';
import './CarePeopleRolePanel.css';

// Sentence case, as the person would read it on a permission list.
const TITLES = {
  audit_signoff: 'Sign off audits',
  careplan_settings: 'Change CarePlan settings',
  careplan_tasks: 'Create or edit care tasks',
  termination_data_export: 'Download the data export when the organisation leaves',
  assets: 'Add and edit assets on the asset register',
  medicines: 'Manage medicines: add and change medicines and dose times, and correct dose records',
};

export default function NamedGrantsPanel({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [removeKey, setRemoveKey] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAuthorityGrants(user.clientUserId));
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleGrant(key) {
    setBusyKey(key);
    try {
      await grantAuthority(user.clientUserId, key);
      toast.success(`Granted: ${TITLES[key]}`);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRemoveConfirm() {
    const key = removeKey;
    setBusyKey(key);
    try {
      await revokeAuthority(user.clientUserId, key);
      toast.success(`Removed: ${TITLES[key]}`);
      setRemoveKey(null);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setBusyKey(null);
    }
  }

  if (loading && !data) {
    return (
      <StatusMessage variant="loading" className="cp-role-panel__state">
        <Loader2 size={16} className="cp-role-panel__spinner" />
        <span>Loading named grants…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="cp-role-panel__state cp-role-panel__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="cp-role-panel__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  if (!data) return null;

  if (!data.grantable) {
    return (
      <section className="cp-role-panel cp-role-panel--na" data-testid="named-grants-na">
        <h3 className="cp-role-panel__heading">Named grants</h3>
        <div className="cp-role-panel__na-note">
          <ShieldCheck size={16} />
          <p>Named grants apply only to managers, admins, and (for medicines) carers.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="cp-role-panel" data-testid="named-grants-panel">
      <h3 className="cp-role-panel__heading">Named grants</h3>
      <p className="cp-role-panel__current-meta">
        {data.onByDefault
          ? 'Managers have all of these by default. Remove one only when the provider asks. A removal is final: CareHub will not give it back automatically.'
          : data.roleKey === 'carer'
            ? (data.careCoordinator
              ? 'As a care coordinator, this person can manage medicines by default. Remove it only when the provider asks; a removal is final.'
              : 'A carer can only be given the medicines grant. Give it to senior staff who add and change medicines.')
            : 'Admins do not get these by default. Grant only what this person needs.'}
      </p>
      <ul className="cp-role-panel__history-list">
        {data.grants.filter((g) => g.grantable !== false).map((g) => {
          // On = an active row, or on by default and never removed (the row
          // lands at sign-in or overnight; 2026-10-24).
          const on = g.active || g.defaultApplies;
          return (
          <li key={g.authorityKey} className="cp-role-panel__history-row" data-testid={`named-grant-${g.authorityKey}`}>
            <span className="cp-role-panel__history-line">
              <Badge variant={on ? 'success' : 'neutral'}>{on ? 'Granted' : 'Not granted'}</Badge>
              <span className="cp-role-panel__history-role">{TITLES[g.authorityKey] || g.label}</span>
              {((g.active && g.grantSource === 'default') || g.defaultApplies) && <Badge variant="teal">Default</Badge>}
            </span>
            <span className="cp-role-panel__history-by">
              {g.active && g.grantedAt && <>Since {formatDate(g.grantedAt)}{g.grantedBy && <> by {g.grantedBy}</>}</>}
              {!g.active && g.removedOnRequest && <>Removed {formatDate(g.removedAt)}{g.removedBy && <> by {g.removedBy}</>}</>}
            </span>
            <span className="cp-role-panel__actions">
              {on ? (
                <button type="button" className="cp-role-panel__revoke" disabled={busyKey === g.authorityKey}
                  onClick={() => setRemoveKey(g.authorityKey)}>
                  Remove
                </button>
              ) : (
                <button type="button" className="cp-role-panel__change" disabled={busyKey === g.authorityKey}
                  onClick={() => handleGrant(g.authorityKey)}>
                  Grant
                </button>
              )}
            </span>
          </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={removeKey != null}
        onClose={() => { if (!busyKey) setRemoveKey(null); }}
        onConfirm={handleRemoveConfirm}
        title={removeKey ? `Remove "${TITLES[removeKey]}"?` : 'Remove?'}
        message={`${user.email || 'This user'} will lose this immediately. The removal is final: CareHub will not give it back automatically. You can grant it again here later.`}
        confirmLabel="Remove"
        isSubmitting={busyKey != null}
      />
    </section>
  );
}
